import type { FormDataBank, FormDataBankSubmit, ItemWithdrawAmount, UserBankItemSelected } from './interface'
import { useRouter } from '#app'
import type { UserBankItem } from '~/composables/account/interface'
import type { BankListItem } from '~/composables/deposit/interface'
import type { SupportedBank } from '~/composables/withdraw/interface'
import { useApi } from '~/composables/common/use-api'
import { DEPOSIT_WITHDRAW_API } from '~/configs/api'
import { useDepositStore } from '~/stores/deposit.store.ts'
import { useSupportedBanksStore } from '~/stores/supported-banks.store.ts'
import { useUserBanksStore } from '~/stores/user-banks.store.ts'
import { usePromotion } from '~/composables/promotion/use-promotion'
import { withdrawAmountList } from '~/resources/withdraw'
import { notificationPopup } from '~utils/modal/notify.ts'
import { toastPopup } from '~/utils/modal/toast'
import { modalList } from '~resources/popup-notification.ts'
import { ResponseStatus } from '~composables/common/interface'
import { DEPOSIT_STATUS } from '~/constants'
import { AccountTabs } from '~/constants/account'

import logoBankDefault from '~/assets/images/pages/account/withdraw/logo-default.svg'

const globImages: GlobImageI = import.meta.glob('~/assets/images/pages/account/bank-list/*', { eager: true })
const iconImgUrl = '/assets/images/pages/account/bank-list'

interface PlaceholderBankItem {
  value: string
  label: string
  image: string
  isDisabled: boolean
  bank_code: string
}
export function useBankWithdraw() {
  const router = useRouter()
  const { request } = useApi()
  const { isUsingPromotion, alertWithdrawlPromotion } = usePromotion()
  const storeWithdrawBanks = useDepositStore()
  const banksStore = useSupportedBanksStore()
  const storeUserBanks = useUserBanksStore()
  const { isMobile } = useDevice()
  const depositAmountSuggests = ref<HTMLDivElement[]>()
  const formData = reactive<FormDataBank>({
    amount: '',
    amount_withdraw_mask: '',
    to_bank_no: '',
    to_bank_name: '',
    to_bank_code: ''
  })

  const mapBankItem = (
    item: UserBankItem | BankListItem,
    globImages: GlobImageI,
    iconImgUrl: string
  ): PlaceholderBankItem => ({
    ...item,
    value: item.bank_code,
    label: item.bank_name,
    isDisabled: false,
    image: getImageURL(globImages, item.bank_code.toLowerCase(), iconImgUrl, 'svg') || logoBankDefault
  })

  const withdrawBanks = computed(() => {
    let banks = []
    if (storeWithdrawBanks?.withdrawBanks.length) {
      banks = storeWithdrawBanks.withdrawBanks.map(item =>
        mapBankItem(item, globImages, iconImgUrl)
      )
    } else {
      banks = banksStore.banks.map(item =>
        mapBankItem(item, globImages, iconImgUrl)
      )
    }
    return isMobile ? banks : [...banks]
  })

  const labelBankName = (bankCode: string): string | undefined => {
    return storeWithdrawBanks?.withdrawBanks?.find(
      bank => bank.bank_code?.toLocaleLowerCase() === bankCode.toLocaleLowerCase()
    )?.bank_name
  }

  const supportedBanks = computed(() => {
    const userBanks = storeUserBanks.userMainBanks?.filter(
      user => user.bank_code && user.bank_account_no && user.bank_account_name
    )
    if (!Object.keys(userBanks).length) {
      return []
    }
    const banks = storeUserBanks.userMainBanks.map(item => ({
      ...item,
      value: item.bank_code,
      label: `${item.bank_name} | ${item.bank_account_no}`,
      labelMobile: labelBankName(item.bank_code) ?? item.bank_code,
      isDisabled: false,
      image: getImageURL(globImages, item.bank_code.toLowerCase(), iconImgUrl, 'svg') || logoBankDefault
    }))
    return isMobile ? banks : [...banks]
  })

  const supportedBanksForDropdown = computed(() =>
    (supportedBanks.value as SupportedBank[]).map(item => {
      if (!item.labelMobile || !item.bank_account_no) {
        return item
      }
      return { ...item, label: `${item.labelMobile} | ${item.bank_account_no}` }
    })
  )

  const selectBank = (selectItem: UserBankItemSelected['item']): void => {
    formData.to_bank_code = selectItem.bank_code
    if (storeUserBanks.userMainBanks.length) {
      formData.to_bank_no = selectItem.bank_account_no
      formData.to_bank_name = selectItem.bank_account_name
    }
  }
  function changeAmountDeposit(value: string): void {
    formData.amount_withdraw_mask = value
    if (value) {
      const indexItem = withdrawAmountList.findIndex(
        (amount: ItemWithdrawAmount) => Number(amount.amount) === Number(value.replace(/,/g, ''))
      )
      if (depositAmountSuggests.value && indexItem >= 0) {
        scrollToActiveCenterItem(depositAmountSuggests.value, indexItem)
      }
    }
  }
  function handleChangeInput(value: string, name: keyof typeof formData): void {
    formData[name] = value
  }

  async function handleSubmit(dataSubmit: FormDataBankSubmit): Promise<void> {
    formData.amount = NumberUtils.formatMarkToNumber(dataSubmit.amountWithdrawMask, 1000)
    formData.to_bank_no = dataSubmit.toBankNo
    formData.to_bank_name = dataSubmit.toBankName
    if (isUsingPromotion.value) {
      alertWithdrawlPromotion()
      return
    }
    try {
      const response = await request(DEPOSIT_WITHDRAW_API.WITHDRAW_BANK, {
        method: 'POST',
        body: formData
      })
      if (response?.status === ResponseStatus.OK) {
        toastPopup({ title: 'Tạo phiếu rút thành công', type: 'success', isMobile })
        void router.push(AccountTabs.HISTORY_TRANSACTION)
        return
      }
      if (response?.status === DEPOSIT_STATUS.ADD_WITHDRAW_INVOICE_FAIL_ERROR) {
        notificationPopup(modalList.WITHDRAW_ERROR)
        return
      }
      if (
        response?.status === DEPOSIT_STATUS.ADD_INVOICE_FAIL_ERROR ||
        response?.status === DEPOSIT_STATUS.INSUFFICIENT_BALANCE_ERROR
      ) {
        notificationPopup(modalList.INSUFFICIENT_BALANCE_ERROR)
      }
    } catch {
      notificationPopup(modalList.ERROR_SYSTEM)
    }
  }

  onMounted(() => {
    if (supportedBanks.value.length && !formData.to_bank_code) {
      selectBank(supportedBanks.value[0])
    }
  })

  return {
    handleChangeInput,
    changeAmountDeposit,
    handleSubmit,
    selectBank,
    formData,
    withdrawBanks,
    supportedBanks,
    supportedBanksForDropdown
  }
}
